import { useCallback } from 'react';
import { FaChevronDown, FaChevronUp, FaBuilding, FaUser, FaTag } from 'react-icons/fa';
import KanbanColumn from './KanbanColumn';
import useSessionState from '@/hooks/useSessionState';
import '@/styles/FunctionSwimlanes.scss';
import '@/styles/ActionItemsSwimlanes.scss';

/**
 * GroupBySwimlanes Component
 *
 * Renders action items in swimlanes grouped by a user-selected dimension:
 * - property   – one swimlane per property
 * - assignee   – one swimlane per assigned user
 * - type       – one swimlane per ActionItemsType / TemplateKey
 *
 * Each swimlane is collapsible and state persists in sessionStorage.
 */
const GroupBySwimlanes = ({
    groupBy,
    items,
    statuses,
    activeStatuses,
    showClosedItems,
    closedItemsLimit,
    onLoadMoreClosed,
    propertiesMDA,
    navigate,
    needsMyAttention,
    getItemIcon,
    getItemDisplayText,
    getUserDisplayName,
    formatCardDate,
    getProjectName,
    allUsers,
    isAssignedToMyDepartment
}) => {
    const [collapsedMap, setCollapsedMap] = useSessionState(`ai_group_swimlane_${groupBy}`, {});

    const toggleCollapsed = useCallback((id) => {
        setCollapsedMap(prev => ({ ...prev, [id]: !prev[id] }));
    }, [setCollapsedMap]);

    const isCollapsed = useCallback((id, isFirst) => {
        if (id in collapsedMap) return collapsedMap[id];
        return !isFirst;
    }, [collapsedMap]);

    /**
     * Build swimlane groups from the items based on the groupBy dimension.
     * Returns an array of { id, label, icon, color, items }.
     */
    const buildGroups = () => {
        const map = new Map();

        items.forEach(item => {
            let key, label, icon, color;

            switch (groupBy) {
                case 'property': {
                    key = item.propertyID ?? 0;
                    const prop = propertiesMDA.find(p => p.propertyID === item.propertyID);
                    label = prop?.propertyName || `Property ${item.propertyID || 'Unknown'}`;
                    icon = <FaBuilding />;
                    color = '#3498db';
                    break;
                }
                case 'assignee': {
                    key = item.assignedToUserID ?? 0;
                    // Look up the full user object by ID so getUserDisplayName receives
                    // a user object (not a bare integer). The /api/User/all endpoint
                    // returns objects with an `id` field — consistent with how the
                    // rest of ActionItemsPage.jsx looks up assigned users.
                    const assignedUser = allUsers.find(u => u.id === key);
                    label = key ? (getUserDisplayName(assignedUser) || 'Unknown User') : 'Unassigned';
                    icon = <FaUser />;
                    color = '#27ae60';
                    break;
                }
                case 'type':
                default: {
                    key = item.actionItemsType || item.templateKey || 'Other';
                    label = key;
                    icon = <FaTag />;
                    color = '#8e44ad';
                    break;
                }
            }

            if (!map.has(key)) {
                map.set(key, { id: String(key), label, icon, color, items: [] });
            }
            map.get(key).items.push(item);
        });

        // Null-guard labels in case any groupBy variant produces a null label for
        // an edge-case item — prevents localeCompare from throwing on null.
        return Array.from(map.values()).sort((a, b) =>
            (a.label ?? '').localeCompare(b.label ?? '')
        );
    };

    const groups = buildGroups();

    const groupByStatus = (groupItems) => {
        const grouped = {};
        statuses.forEach(status => {
            grouped[status] = groupItems.filter(i => i.status === status);
        });
        return grouped;
    };

    const renderCollapsedSummary = (groupItems) => {
        if (!groupItems || groupItems.length === 0) return null;
        return (
            <div className="swimlane-collapsed-summary">
                {(showClosedItems ? statuses : activeStatuses).map(status => {
                    const count = groupItems.filter(i => i.status === status).length;
                    if (count === 0) return null;
                    return (
                        <span key={status} className={`summary-badge status-${status.toLowerCase().replace('-', '')}`}>
                            {count}
                        </span>
                    );
                })}
            </div>
        );
    };

    if (groups.length === 0) {
        return (
            <div className="swimlane-empty-state">
                <p>No items found for the current filters.</p>
            </div>
        );
    }

    return (
        <div className="swimlanes-container">
            {/* Sticky status labels row */}
            <div className="swimlanes-status-headers">
                {(showClosedItems ? statuses : activeStatuses).map(status => (
                    <div
                        key={status}
                        className={`swimlane-status-label status-${status.replace(/\s+/g, '').replace(/-/g, '').toLowerCase()}`}
                    >
                        {status}
                    </div>
                ))}
            </div>

            <div className="function-swimlanes">
            {groups.map((group, index) => {
                const byStatus = groupByStatus(group.items);
                const collapsed = isCollapsed(group.id, index === 0);

                return (
                    <div key={group.id} className={`swimlane ${collapsed ? 'collapsed' : ''}`}>
                        <div
                            className="swimlane-header clickable"
                            style={{ borderLeftColor: group.color }}
                            onClick={() => toggleCollapsed(group.id)}
                        >
                            <button
                                className="swimlane-toggle-btn"
                                onClick={(e) => { e.stopPropagation(); toggleCollapsed(group.id); }}
                                aria-label={collapsed ? 'Expand swimlane' : 'Collapse swimlane'}
                            >
                                {collapsed ? <FaChevronDown /> : <FaChevronUp />}
                            </button>
                            <div className="swimlane-indicator" style={{ backgroundColor: group.color }} />
                            <div className="swimlane-label">
                                {group.icon}
                                <span>{group.label}</span>
                                <span className="swimlane-count">
                                    ({group.items.length})
                                </span>
                            </div>
                            {collapsed && renderCollapsedSummary(group.items)}
                        </div>
                        {!collapsed && (
                            <div className="swimlane-body">
                                {(showClosedItems ? statuses : activeStatuses).map(status => {
                                    const columnItems = status === "Closed" && showClosedItems
                                        ? byStatus[status].slice(0, closedItemsLimit)
                                        : byStatus[status];
                                    const totalClosedItems = status === "Closed" ? byStatus[status].length : 0;
                                    const hasMoreClosedItems = status === "Closed" && totalClosedItems > closedItemsLimit;

                                    return (
                                        <KanbanColumn
                                            key={`${group.id}-${status}`}
                                            status={status}
                                            droppableId={`groupby-${group.id}-${status}`}
                                            items={columnItems}
                                            propertiesMDA={propertiesMDA}
                                            navigate={navigate}
                                            hasMore={hasMoreClosedItems}
                                            totalItems={totalClosedItems}
                                            onLoadMore={onLoadMoreClosed}
                                            needsMyAttention={needsMyAttention}
                                            getItemIcon={getItemIcon}
                                            getItemDisplayText={getItemDisplayText}
                                            getUserDisplayName={getUserDisplayName}
                                            formatCardDate={formatCardDate}
                                            getProjectName={getProjectName}
                                            allUsers={allUsers}
                                            isAssignedToMyDepartment={isAssignedToMyDepartment}
                                            hideHeader={true}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
            </div>
        </div>
    );
};

export default GroupBySwimlanes;
